import React from 'react';
import { styled, css } from '@shared/joykit/packages/core/common/styledComponents';
import type { Props } from '../../common/props';
import type { InputV1Props } from '.';
import { generateComponentDisplayName, animationTransition } from '../../utils';
import { getStyledBaseComponentProps } from '../../common/styledSystem/styledFunctions';
import type { PaletteColorTokens } from '../../common';
import { FIELD_BORDER_WIDTH } from './StyledInput';

const Container: React.FC<
  {
    disabled: InputV1Props['disabled'];
    invalid: boolean;
    minimal?: boolean;
    focused: boolean;
    suppressFocusRing?: boolean;
    focusedBorderColor?: keyof PaletteColorTokens;
  } & Props
> = ({ children, className }) => {
  return <div className={className}>{children}</div>;
};

export const StyledInputContainer = styled(Container)`
  position: relative;
  color: ${props => (props.disabled ? props.theme.colors.typeDisabledDark : undefined)};

  ${props => {
    if (props.disabled) {
      return {
        cursor: 'not-allowed',
      };
    }

    // `minimal` suppresses the field's *border*, not its focus indicator, so the ring survives it.
    // `suppressFocusRing` is the explicit opt-out for composites that supply their own `_focusWithin`
    // and would otherwise double-ring.
    if (props.suppressFocusRing) {
      return null;
    }

    const { colors, radii } = props.theme;
    // `focusedBorderColor` is a deprecated per-site escape hatch, retired in a later phase. It no
    // longer drives hover -- only the state it is named for.
    const focusColor = (props.focusedBorderColor && colors[props.focusedBorderColor]) || colors.inputBorderFocused;
    const activeColor = props.invalid ? colors.inputBorderError : focusColor;
    const isActive = props.invalid || props.focused;

    // The ring has to overlap the field's hairline, not abut it. This container has no border of its
    // own, so `inset: 0` lands on the input's *border box* -- the ring then starts exactly where the
    // hairline ends, and the two rounded rects meeting edge-to-edge leave a pale seam at the corners
    // where their curves disagree by a sub-pixel. Insetting by the border width puts the 2px spread
    // on top of the hairline and 1px past it, which is the footprint `InputV2` gets for free because
    // there the ring and the border share one element. `minimal` has no hairline to cover.
    const ringInset = props.minimal ? '0' : FIELD_BORDER_WIDTH;

    return css`
      &::after {
        content: ' ';
        display: block;
        position: absolute;
        top: ${ringInset};
        left: ${ringInset};
        right: ${ringInset};
        bottom: ${ringInset};
        border-radius: ${radii.field};
        box-shadow: 0 0 0 2px ${isActive ? activeColor : 'transparent'};
        pointer-events: none;
        transition: ${animationTransition('box-shadow')};
      }

      /* Hover and focus share one ring, so hovering a focused field must not demote its colour. */
      &:hover::after {
        box-shadow: 0 0 0 2px ${isActive ? activeColor : colors.mono6};
      }
    `;
  }}

  ${getStyledBaseComponentProps}
`;

StyledInputContainer.displayName = generateComponentDisplayName('Input__Container');
